import React, { useEffect, useState } from 'react';
import {
  getInterviewConfig,
  resetInterviewConfig,
  fetchInterviewConfig,
  saveInterviewConfigRemote,
} from '../interview/config/interviewConfig';
import { getMetadata } from '../../utils/encryptedStorage';

/**
 * Admin: control the candidate interview flow — which onboarding steps appear, what is verified,
 * what is recorded, the interview requirements, and the theme. Saves to the backend
 * (`ai_interview_configs`, scoped to this company), so changes reach candidates on every device.
 */
function getCompanyId() {
  try {
    const metadata = getMetadata();
    return String(metadata?.cid || metadata?.companyId || metadata?.company_id || '0');
  } catch { return '0'; }
}

const Toggle = ({ checked, onChange, label, hint, disabled = false }) => (
  <label className={`flex items-start justify-between gap-4 rounded-xl border px-4 py-3 transition ${disabled
    ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-70 dark:border-gray-800 dark:bg-gray-900/40'
    : checked
      ? 'cursor-pointer border-primary-200 bg-primary-50/40 dark:border-primary-800 dark:bg-primary-900/20'
      : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800'}`}>
    <span>
      <span className="block text-sm font-medium text-gray-800 dark:text-gray-100">{label}</span>
      {hint && <span className="mt-0.5 block text-xs text-gray-400 dark:text-gray-500">{hint}</span>}
    </span>
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition ${disabled ? 'cursor-not-allowed' : ''} ${checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
      aria-pressed={checked}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${checked ? 'left-[1.375rem]' : 'left-0.5'}`} />
    </button>
  </label>
);

const Section = ({ title, desc, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
      {desc && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{desc}</p>}
    </div>
    <div className="grid gap-2.5 sm:grid-cols-2">{children}</div>
  </div>
);

const AdminInterviewConfigPage = () => {
  const [cfg, setCfg] = useState(getInterviewConfig);  // cached value for instant paint
  const [msg, setMsg] = useState(null);     // { type, text }
  const [busy, setBusy] = useState(false);
  // Which config is being edited: 'common' = the admin-wide config that applies to EVERY company;
  // 'company' = one company's override (its id typed below). Empty target id → common (cid 0).
  const [scope, setScope] = useState('common');
  const [scopeCid, setScopeCid] = useState('');    // company id typed in Company-specific mode
  const [loadedCid, setLoadedCid] = useState('');   // the company id the shown config was loaded for
  const targetCid = scope === 'common' ? '' : String(scopeCid || '').trim();

  const loadScope = async (cid) => {
    setBusy(true);
    try {
      const c = await fetchInterviewConfig(String(cid || ''), '', '');
      setCfg(c);
      setLoadedCid(String(cid || ''));
      setMsg(null);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Could not load configuration.' });
    } finally { setBusy(false); }
  };

  // On mount, load the COMMON config (the admin-wide default every company inherits).
  useEffect(() => {
    let alive = true;
    (async () => {
      const c = await fetchInterviewConfig('', '', '');   // '' → common (cid 0)
      if (alive) { setCfg(c); setLoadedCid(''); }
    })();
    return () => { alive = false; };
  }, []);

  const set = (group, key, value) =>
    setCfg((c) => ({ ...c, [group]: { ...(c[group] || {}), [key]: value } }));

  // PERMISSION (per device) is the top-level gate: the candidate is asked for a device only if its access
  // is on, and everything else for that device — recording, "strict", and the photo/voice verification
  // that needs it — only applies once access is granted. Turning a device's access OFF therefore clears
  // its recording + strict + dependent verification, so the stored config can never contradict itself
  // (e.g. "record the screen" while the screen is never even requested).
  const setAccess = (device, value) =>
    setCfg((c) => {
      const interview = { ...(c.interview || {}) };
      const record = { ...(c.record || {}) };
      const verify = { ...(c.verify || {}) };
      if (device === 'camera') {
        interview.camera_required = value;
        if (!value) { record.camera = false; interview.camera_strict = false; verify.photo = false; }
      } else if (device === 'screen') {
        interview.screen_required = value;
        if (!value) { record.screen = false; interview.screen_strict = false; }
      } else if (device === 'mic') {
        interview.mic_required = value;
        if (!value) { record.audio = false; verify.voice = false; }
      }
      return { ...c, interview, record, verify };
    });

  // Belt-and-braces before persisting: nothing can be recorded / strict / verified for a device whose
  // access is off. Written back so even a legacy/stale config is always self-consistent.
  const normalize = (c) => {
    const iv = c.interview || {}; const rec = c.record || {}; const vf = c.verify || {};
    const cam = iv.camera_required !== false, mic = iv.mic_required !== false, scr = iv.screen_required !== false;
    return {
      ...c,
      interview: {
        ...iv,
        camera_required: cam, mic_required: mic, screen_required: scr,
        camera_strict: cam ? !!iv.camera_strict : false,
        screen_strict: scr ? !!iv.screen_strict : false,
      },
      record: {
        ...rec,
        camera: cam ? !!rec.camera : false,
        screen: scr ? !!rec.screen : false,
        audio: mic ? !!rec.audio : false,
      },
      verify: {
        ...vf,
        photo: cam ? !!vf.photo : false,
        voice: mic ? !!vf.voice : false,
      },
      retention: {
        recording_days: Math.max(1, parseInt(c.retention?.recording_days, 10) || 30),
        keep_forever: !!(c.retention?.keep_forever),
      },
    };
  };

  const save = async () => {
    if (scope === 'company' && !targetCid) { setMsg({ type: 'error', text: 'Enter a company ID first (or switch to Common).' }); return; }
    if (scope === 'company' && loadedCid !== targetCid) { setMsg({ type: 'error', text: `Click "Load" for company ${targetCid} first, so you edit its real config.` }); return; }
    setBusy(true);
    try {
      const clean = normalize(cfg);
      setCfg(clean);
      await saveInterviewConfigRemote(clean, targetCid);
      setMsg({ type: 'success', text: targetCid
        ? `Saved as an override for company ${targetCid} — only its interviews use this (takes precedence over Common).`
        : 'Saved as the COMMON config — every company uses it unless it has its own override.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Could not save to the server.' });
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (scope === 'company' && !targetCid) { setMsg({ type: 'error', text: 'Enter a company ID first (or switch to Common).' }); return; }
    const d = resetInterviewConfig();
    setCfg(d);
    setBusy(true);
    try {
      await saveInterviewConfigRemote(d, targetCid);
      setMsg({ type: 'success', text: targetCid ? `Reset company ${targetCid} to defaults.` : 'Reset the Common config to defaults.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Reset here, but could not save to the server.' });
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="p-8 text-rose-600 dark:text-rose-400">No configuration.</div>;

  const steps = cfg.steps || {};
  const verify = cfg.verify || {};
  const record = cfg.record || {};
  const interview = cfg.interview || {};
  const retention = cfg.retention || {};
  // Per-device access = the primary gate everything else hangs off (default on).
  const camOn = interview.camera_required !== false;
  const micOn = interview.mic_required !== false;
  const screenOn = interview.screen_required !== false;

  return (
    <div className="px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Interview Flow Configuration</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Controls the candidate experience for every new interview (WIZBOX · Wiza). Changes apply to interviews started after saving.
        </p>
      </div>

      {/* Scope: the admin-wide common config vs a single company's override */}
      <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Editing:</span>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input type="radio" name="cfgscope" checked={scope === 'common'} onChange={() => { setScope('common'); loadScope(''); }} />
            Common — all companies
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input type="radio" name="cfgscope" checked={scope === 'company'} onChange={() => setScope('company')} />
            A specific company
          </label>
          {scope === 'company' && (
            <span className="flex items-center gap-2">
              <input type="text" inputMode="numeric" placeholder="Company ID" value={scopeCid}
                onChange={(e) => setScopeCid(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-28 rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
              <button type="button" onClick={() => scopeCid && loadScope(scopeCid)} disabled={!scopeCid || busy}
                className="rounded-lg bg-gray-800 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-gray-700 disabled:opacity-50 dark:bg-gray-700 dark:hover:bg-gray-600">
                Load
              </button>
            </span>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {scope === 'common'
            ? 'The admin-wide config. Every company uses these settings unless it has its own override.'
            : (loadedCid && loadedCid === targetCid)
              ? `Editing an override for company ${targetCid}. Saving affects only this company and takes precedence over Common.`
              : 'Enter a company ID and click Load to edit that company’s override (it starts from the Common settings).'}
        </p>
      </div>

      <div className="space-y-5">
        <Section title="Onboarding steps" desc="Which screens the candidate sees before the interview.">
          <Toggle label="Permission & readiness check" hint="Pre-flight for the devices you request below" checked={!!steps.permission} onChange={(v) => set('steps', 'permission', v)} />
          <Toggle label="Candidate verification" hint="Photo, voice, network" checked={!!steps.verification} onChange={(v) => set('steps', 'verification', v)} />
          <Toggle label="Instructions" hint="Rules + 'I understand' consent" checked={!!steps.instruction} onChange={(v) => set('steps', 'instruction', v)} />
        </Section>

        <Section title="Device permissions"
          desc="What the candidate is asked to allow — the master switch per device. Everything below (verification checks and recording/proctoring) only applies to a device you turn on here.">
          <Toggle label="Camera access"
            hint={camOn ? 'Candidate is asked to allow their camera' : 'Camera is never requested'}
            checked={camOn} onChange={(v) => setAccess('camera', v)} />
          <Toggle label="Microphone access"
            hint={micOn ? 'Candidate is asked to allow their mic (Wiza needs to hear them)' : 'Off — text-only interview, no mic requested'}
            checked={micOn} onChange={(v) => setAccess('mic', v)} />
          <Toggle label="Screen share"
            hint={screenOn ? 'Candidate is asked to share their entire screen' : 'Screen share is never requested'}
            checked={screenOn} onChange={(v) => setAccess('screen', v)} />
        </Section>

        <Section title="Verification" desc="Identity / connection checks shown in the verification step.">
          <Toggle label="Photo capture" hint={camOn ? 'One-time ID photo — uses the camera' : 'Needs Camera access (off)'} checked={!!verify.photo && camOn} disabled={!camOn} onChange={(v) => set('verify', 'photo', v)} />
          <Toggle label="Voice verification" hint={micOn ? 'Read-aloud check — uses the microphone' : 'Needs Microphone access (off)'} checked={!!verify.voice && micOn} disabled={!micOn} onChange={(v) => set('verify', 'voice', v)} />
          <Toggle label="Network quality test" hint="Connection speed / stability" checked={!!verify.network} onChange={(v) => set('verify', 'network', v)} />
        </Section>

        <Section title="Recording & proctoring"
          desc="How each allowed device is used during the interview. These unlock only for a device you turned on in Device permissions.">
          {/* Camera */}
          <Toggle label="Record the camera" hint={camOn ? "Save the candidate's webcam video" : 'Turn on Camera access first'}
            checked={!!record.camera} disabled={!camOn} onChange={(v) => set('record', 'camera', v)} />
          <Toggle label="Camera strict" hint={camOn ? "Can't turn the camera off mid-interview" : 'Turn on Camera access first'}
            checked={!!interview.camera_strict} disabled={!camOn} onChange={(v) => set('interview', 'camera_strict', v)} />
          {/* Screen */}
          <Toggle label="Record the screen" hint={screenOn ? 'Save the shared screen' : 'Turn on Screen share first'}
            checked={!!record.screen} disabled={!screenOn} onChange={(v) => set('record', 'screen', v)} />
          <Toggle label="Screen strict" hint={screenOn ? "Can't stop sharing mid-interview" : 'Turn on Screen share first'}
            checked={!!interview.screen_strict} disabled={!screenOn} onChange={(v) => set('interview', 'screen_strict', v)} />
          {/* Microphone */}
          <Toggle label="Record the audio" hint={micOn ? "Save the candidate's spoken answers" : 'Turn on Microphone access first'}
            checked={!!record.audio} disabled={!micOn} onChange={(v) => set('record', 'audio', v)} />
        </Section>

        <Section title="Recording retention"
          desc="How long recordings are kept before they're automatically deleted. Video (screen/camera) and the voice verification clip are removed after this many days from when they were created; photos, transcript and the report are always kept.">
          <label className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-3.5 py-3 dark:border-gray-700">
            <span>
              <span className="block text-sm font-medium text-gray-900 dark:text-white">Keep recordings for</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">Days before auto-deletion (default 30)</span>
            </span>
            <span className="flex items-center gap-1.5">
              <input type="number" min="1" max="3650"
                value={retention.keep_forever ? '' : (retention.recording_days ?? 30)}
                disabled={!!retention.keep_forever}
                onChange={(e) => set('retention', 'recording_days', Math.max(1, parseInt(e.target.value, 10) || 30))}
                className="w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              <span className="text-sm text-gray-500 dark:text-gray-400">days</span>
            </span>
          </label>
          <Toggle label="Keep forever" hint="Never auto-delete recordings for this scope"
            checked={!!retention.keep_forever} onChange={(v) => set('retention', 'keep_forever', v)} />
        </Section>

        <Section title="Theme" desc="The look candidates see during onboarding and the interview.">
          <div className="col-span-full flex gap-2">
            {['dark', 'light', 'auto'].map((m) => (
              <button key={m} type="button" onClick={() => setCfg((c) => ({ ...c, theme_mode: m }))}
                className={`rounded-lg px-4 py-2 text-sm font-medium capitalize transition ${cfg.theme_mode === m
                  ? 'bg-primary-600 text-white shadow'
                  : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'}`}>
                {m}
              </button>
            ))}
          </div>
        </Section>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button type="button" onClick={save} disabled={busy}
          className="rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-60">
          {busy ? 'Saving…' : 'Save configuration'}
        </button>
        <button type="button" onClick={reset} disabled={busy}
          className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">
          Reset to defaults
        </button>
        {msg && (
          <span className={`text-sm ${msg.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{msg.text}</span>
        )}
      </div>
    </div>
  );
};

export default AdminInterviewConfigPage;
